import { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ListChecks, Trophy } from "lucide-react";

export default function PlanTracker({ reports, loading, onChanged }) {
  const [busy, setBusy] = useState(null);

  // Flatten plan items across reports, newest date first
  const groups = useMemo(() => {
    const sorted = [...(reports || [])].sort((a, b) => (a.date < b.date ? 1 : -1));
    return sorted
      .map((r) => ({
        reportId: r.id,
        date: r.date,
        items: (r.plan_tomorrow || []).filter((p) => typeof p === "object" && p.id),
      }))
      .filter((g) => g.items.length > 0);
  }, [reports]);

  const totalItems = groups.reduce((s, g) => s + g.items.length, 0);
  const doneItems = groups.reduce((s, g) => s + g.items.filter((i) => i.done).length, 0);

  const toggle = async (reportId, itemId, done) => {
    setBusy(itemId);
    try {
      await api.patch(`/reports/${reportId}/plan/${itemId}`, { done });
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update");
    } finally {
      setBusy(null);
    }
  };

  const saveRemark = async (reportId, itemId, remark, original) => {
    if (remark.trim() === (original || "")) return;
    setBusy(itemId);
    try {
      await api.patch(`/reports/${reportId}/plan/${itemId}`, { remark });
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save remark");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  if (groups.length === 0) {
    return (
      <Card className="p-12 text-center rounded-xl" data-testid="tracker-empty">
        <ListChecks className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
        <p className="text-muted-foreground">No plans yet. Add "Plan for tomorrow" items in your report to track them here.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="plan-tracker">
      <Card className="p-5 rounded-xl flex items-center gap-4">
        <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
          <Trophy className="h-6 w-6" />
        </div>
        <div>
          <p className="font-heading text-2xl font-bold">{doneItems}/{totalItems}</p>
          <p className="text-xs text-muted-foreground">planned items completed</p>
        </div>
      </Card>

      {groups.map((g) => (
        <Card key={g.reportId} className="p-5 rounded-xl" data-testid={`tracker-group-${g.date}`}>
          <p className="text-xs font-mono text-muted-foreground mb-3">Planned on {g.date}</p>
          <ul className="space-y-2">
            {g.items.map((it) => (
              <li key={it.id} className="flex items-start gap-3" data-testid={`plan-item-${it.id}`}>
                <Checkbox
                  checked={!!it.done}
                  disabled={busy === it.id}
                  onCheckedChange={(v) => toggle(g.reportId, it.id, !!v)}
                  className="mt-0.5"
                  data-testid={`plan-check-${it.id}`}
                />
                <div className="min-w-0 flex-1">
                  <span className={`text-sm break-words ${it.done ? "line-through text-muted-foreground" : "text-foreground/90"}`}>
                    {it.text}
                  </span>
                  {it.origin_date && it.origin_date !== g.date && (
                    <span className="ml-1.5 text-[10px] font-medium text-amber-700 bg-amber-500/15 rounded-full px-1.5 py-0.5">since {it.origin_date}</span>
                  )}
                  <Input
                    defaultValue={it.remark || ""}
                    key={`${it.id}-${it.remark || ""}`}
                    disabled={busy === it.id}
                    onBlur={(e) => saveRemark(g.reportId, it.id, e.target.value, it.remark)}
                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    placeholder="Add a remark…"
                    className="mt-1 h-7 text-[11px] border-transparent bg-transparent px-1.5 hover:border-border focus:border-border"
                    data-testid={`plan-remark-${it.id}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}
